// Google Identity Services wrapper. Loads the GSI script once, then opens
// the One-Tap or button flow to return an ID token. Read clientId from the
// Vite env at build time.
//
// We use the GSI button + One-Tap flows. Backend verifies the JWT against
// Google's JWKS.

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

export function googleClientId() {
  return import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
}

export function googleEnabled() {
  return !!googleClientId();
}

let _gisReady = null;
function ensureGsi() {
  if (_gisReady) return _gisReady;
  _gisReady = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('no_window'));
    if (window.google?.accounts?.id) return resolve(window.google);
    const s = document.createElement('script');
    s.src = SCRIPT_SRC; s.async = true; s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(s);
  });
  return _gisReady;
}

export async function signInWithGoogle() {
  if (!googleEnabled()) throw new Error('Google sign-in not configured. Set VITE_GOOGLE_CLIENT_ID.');
  const g = await ensureGsi();
  return new Promise((resolve, reject) => {
    g.accounts.id.initialize({
      client_id: googleClientId(),
      callback: (resp) => {
        if (resp?.credential) resolve({ id_token: resp.credential });
        else resolve({ id_token: '' });
      },
      auto_select: false,
      cancel_on_tap_outside: true,
      use_fedcm_for_prompt: true,
    });
    // Render the prompt; if it's not shown, fall back to the explicit button.
    g.accounts.id.prompt((notification) => {
      const reason = notification?.getNotDisplayedReason?.();
      const skipped = notification?.getSkippedReason?.();
      if (reason || skipped) {
        // Couldn't show the prompt — caller should render a fallback button.
        // For now, reject so the UI shows an error and the user can retry.
        reject(new Error('Google sign-in was blocked. Allow third-party cookies for accounts.google.com and try again.'));
      }
    });
  });
}
